/**
 * Admin — Library sub-feature manifest.
 *
 * Pools globaux de Steps / Rules / Checkboxes réutilisables. Les formulaires
 * d'étape et de checkbox sont des pages ; celui de règle reste en panneau droit.
 * Pas de lien sidebar — accédé depuis AdminNavView.
 */

import { LayoutList, Pencil } from 'lucide-react';
import { ADMIN_GUARD } from '../admin-guard';
import { CONTEXTS, lazyView, type FeatureManifest } from '@/app/registry';
import { adminLibraryRoutes } from './routes';

const navTab = {
  id: 'admin-nav',
  label: 'Navigation',
  icon: LayoutList,
  view: lazyView(() => import('../views/AdminNavView'), 'AdminNavView'),
};

const ruleFormTab = {
  id: 'admin-rule-form',
  label: 'Règle',
  icon: Pencil,
  view: lazyView(() => import('../views/AdminRuleFormView'), 'AdminRuleFormView'),
};

export const adminLibraryManifest: FeatureManifest = {
  id: 'admin-library',
  contexts: [
    CONTEXTS.ADMIN_STEPS_LIBRARY,
    CONTEXTS.ADMIN_STEP_FORM,
    CONTEXTS.ADMIN_RULES_LIBRARY,
    CONTEXTS.ADMIN_CHECKBOXES_LIBRARY,
    CONTEXTS.ADMIN_CHECKBOX_FORM,
  ],

  routes: [{ routes: adminLibraryRoutes, errorFallbackLabel: 'Bibliothèque', guard: ADMIN_GUARD }],

  centerView: {
    [CONTEXTS.ADMIN_STEPS_LIBRARY]: lazyView(
      () => import('../views/AdminStepsLibraryView'),
      'AdminStepsLibraryView',
    ),
    // Comme la checkbox, le formulaire d'étape est une page : il porte aussi les
    // règles rattachées à l'étape et leurs checkboxes.
    [CONTEXTS.ADMIN_STEP_FORM]: lazyView(
      () => import('../views/AdminStepFormView'),
      'AdminStepFormView',
    ),
    [CONTEXTS.ADMIN_RULES_LIBRARY]: lazyView(
      () => import('../views/AdminRulesLibraryView'),
      'AdminRulesLibraryView',
    ),
    [CONTEXTS.ADMIN_CHECKBOXES_LIBRARY]: lazyView(
      () => import('../views/AdminCheckboxesLibraryView'),
      'AdminCheckboxesLibraryView',
    ),
    // Le formulaire checkbox est désormais une page à part entière (center view),
    // plus un onglet du panneau droit — le formulaire a grossi et n'y tenait plus.
    [CONTEXTS.ADMIN_CHECKBOX_FORM]: lazyView(
      () => import('../views/AdminCheckboxFormView'),
      'AdminCheckboxFormView',
    ),
  },

  panels: {
    left: {
      tabs: {
        [CONTEXTS.ADMIN_STEPS_LIBRARY]: [navTab],
        [CONTEXTS.ADMIN_STEP_FORM]: [navTab],
        [CONTEXTS.ADMIN_RULES_LIBRARY]: [navTab],
        [CONTEXTS.ADMIN_CHECKBOXES_LIBRARY]: [navTab],
        [CONTEXTS.ADMIN_CHECKBOX_FORM]: [navTab],
      },
    },
    right: {
      tabs: {
        [CONTEXTS.ADMIN_RULES_LIBRARY]: [ruleFormTab],
      },
    },
  },

  enabledWhen: {
    roles: ['admin'],
  },

  panelRequirements: {
    [CONTEXTS.ADMIN_STEPS_LIBRARY]: {
      left: { open: true, defaultActiveTab: 'admin-nav' },
      right: { open: false },
    },
    [CONTEXTS.ADMIN_STEP_FORM]: {
      left: { open: true, defaultActiveTab: 'admin-nav' },
      right: { open: false },
    },
    [CONTEXTS.ADMIN_RULES_LIBRARY]: {
      left: { open: true, defaultActiveTab: 'admin-nav' },
      right: { open: false, defaultActiveTab: 'admin-rule-form' },
    },
    [CONTEXTS.ADMIN_CHECKBOXES_LIBRARY]: {
      left: { open: true, defaultActiveTab: 'admin-nav' },
      right: { open: false },
    },
    [CONTEXTS.ADMIN_CHECKBOX_FORM]: {
      left: { open: true, defaultActiveTab: 'admin-nav' },
      right: { open: false },
    },
  },
};
